import {
  type ClassificationSource,
  type ConversationKind,
  type ConversationStatus,
  type Folder,
  type Intent,
  type PageMeta,
  QueueItem,
  QueueItemDetail,
  QueueUnreadSummary,
} from '@rp/contracts'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import type { ApiClient } from '../../api/client'
import { useReadySession } from '../../session/useSession'

export { QUEUE_DEFAULT_STATUS } from '@rp/contracts'

export const QUEUE_PAGE_SIZE = 20
export const QUEUE_UNREAD_STALE_TIME_MS = 30_000

export type QueueFilter = {
  folder: Folder
  status: ConversationStatus
  intent?: Intent
  kind?: ConversationKind
  source?: ClassificationSource
  author?: string
  page: number
  limit: number
}

export const queueKeys = {
  all: ['queue'] as const,
  unread: (tenantId: string) => ['queue', 'unread', tenantId] as const,
  list: (tenantId: string, filter: QueueFilter) =>
    [
      'queue',
      'list',
      tenantId,
      filter.folder,
      filter.status,
      filter.intent ?? null,
      filter.kind ?? null,
      filter.source ?? null,
      filter.author ?? null,
      filter.page,
      filter.limit,
    ] as const,
  detail: (tenantId: string, id: string) => ['queue', 'detail', tenantId, id] as const,
}

export const fetchQueue = async (api: ApiClient, filter: QueueFilter): Promise<{ items: QueueItem[]; meta: PageMeta }> => {
  const result = await api.request(z.array(QueueItem), {
    method: 'GET',
    path: '/v1/queue',
    query: {
      folder: filter.folder,
      status: filter.status,
      intent: filter.intent,
      kind: filter.kind,
      source: filter.source,
      author: filter.author,
      page: filter.page,
      limit: filter.limit,
    },
  })
  return { items: result.data, meta: result.meta ?? { total: result.data.length, page: filter.page, limit: filter.limit } }
}

export const fetchQueueItem = async (api: ApiClient, id: string): Promise<QueueItemDetail> =>
  (await api.request(QueueItemDetail, { method: 'GET', path: `/v1/queue/${id}` })).data

export const fetchQueueUnread = async (api: ApiClient): Promise<QueueUnreadSummary> =>
  (await api.request(QueueUnreadSummary, { method: 'GET', path: '/v1/queue/unread' })).data

export const useQueueList = (filter: QueueFilter) => {
  const { api, membership } = useReadySession()
  return useQuery({
    queryKey: queueKeys.list(membership.tenantId, filter),
    queryFn: () => fetchQueue(api, filter),
    placeholderData: (previous) => previous,
  })
}

type ItemRefetchInterval = number | false | ((item: QueueItemDetail | undefined) => number | false)

export const useQueueItem = (id: string, options: { refetchInterval?: ItemRefetchInterval } = {}) => {
  const { api, membership } = useReadySession()
  const interval = options.refetchInterval ?? false
  return useQuery({
    queryKey: queueKeys.detail(membership.tenantId, id),
    queryFn: () => fetchQueueItem(api, id),
    refetchInterval: typeof interval === 'function' ? (query) => interval(query.state.data) : interval,
  })
}

export const useQueueUnread = () => {
  const { api, membership } = useReadySession()
  return useQuery({
    queryKey: queueKeys.unread(membership.tenantId),
    queryFn: () => fetchQueueUnread(api),
    staleTime: QUEUE_UNREAD_STALE_TIME_MS,
  })
}
